/**
 * <ConfirmDialog> — a centered confirm/cancel dialog (molecule, L0.1).
 *
 * The maison replacement for the legacy `DialogModal`/`DialogM` confirm: a small, auto-height card
 * centered on the shared `<Overlay>` shell, with a title, an optional message, and two `<Button>`
 * actions (ghost cancel + solid confirm). `danger` (default) paints the confirm button `danger` for
 * destructive actions like "Retirer".
 *
 * `children` (optional) sits between the message and the buttons — un guide en quelques gestes
 * (`<DeviceCheckGuidance>`), quand une phrase ne suffit pas.
 *
 * `secondary` (optionnel) : une deuxième réponse entre la confirmation et l'annulation (« Quitter
 * sans clôturer »). Trois réponses ne tiennent plus côte à côte : elles s'empilent, pleine largeur,
 * sur une carte un peu plus large. Le geste en cours fait tourner son bouton (`confirmLoading`,
 * `secondary.loading`) et retient les autres.
 *
 * Composes `<Overlay>` + `<Title>` / `<Text>` + `<Button>` — no hand-rolled overlay or buttons.
 *
 * `recap` (opt-in) en fait la question d'un parcours parent : « Confirmer votre choix ? » centré,
 * ce qui va se passer dans l'encart `<RecapList>`, puis les deux boutons empilés pleine largeur —
 * « Confirmer » plein au-dessus, « Modifier » en secondaire dessous. Sans lui, rendu inchangé.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Overlay } from "../../atoms/Overlay"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { type UIRecapLine } from "../../../ports/primary/ui/types"
import { RecapList } from "../RecapList"

export type ConfirmDialogProps = {
    visible: boolean
    title: string
    /** Optional supporting line below the title. */
    message?: string
    /** Confirm button label — defaults to "Retirer". */
    confirmLabel?: string
    /** Cancel button label — defaults to "Annuler". */
    cancelLabel?: string
    onConfirm: () => void
    onCancel: () => void
    /**
     * Backdrop / hardware-back dismissal — defaults to `onCancel`.
     * Pass it when cancel is itself an action (e.g. "Email") rather than a way out.
     */
    onDismiss?: () => void
    /** Paints the confirm button `danger` (destructive). Defaults to true. */
    danger?: boolean
    /**
     * Retire le bouton d'annulation : la boîte ANNONCE au lieu de demander (« Non autorisé »,
     * le `DialogM` legacy sans `buttonText2`, qui ne rend qu'un « OK »). Défaut `false` — rendu
     * inchangé pour tous les appelants existants.
     */
    hideCancel?: boolean
    /**
     * Ce que la confirmation va faire, ligne par ligne (`<RecapList>`) : le titre se centre et les
     * boutons s'empilent, la confirmation au-dessus. Absent — rendu inchangé.
     */
    recap?: UIRecapLine[]
    /** Sous le message, avant les boutons : ce que la boîte montre en plus d'une phrase. */
    children?: React.ReactNode
    /** Une deuxième réponse, au contour : les réponses s'empilent. Absente → rendu inchangé. */
    secondary?: { label: string; onPress: () => void; loading?: boolean }
    /** La confirmation est en cours : son bouton tourne, les autres attendent. Défaut `false`. */
    confirmLoading?: boolean
    testID?: string
}

export function ConfirmDialog(props: ConfirmDialogProps) {
    const {
        visible,
        title,
        message,
        confirmLabel = "Retirer",
        cancelLabel = "Annuler",
        onConfirm,
        onCancel,
        onDismiss,
        danger = true,
        hideCancel = false,
        recap,
        children,
        secondary,
        confirmLoading = false,
        testID,
    } = props
    const secondaryLoading = secondary?.loading === true
    const busy = confirmLoading || secondaryLoading

    const confirm = (
        <Button
            label={confirmLabel}
            color={danger ? "danger" : undefined}
            fullWidth={secondary != null}
            loading={confirmLoading}
            disabled={busy && !confirmLoading}
            onPress={onConfirm}
        />
    )
    const cancel = hideCancel ? null : (
        <Button
            label={cancelLabel}
            variant="ghost"
            color={danger ? "primary" : undefined}
            fullWidth={secondary != null}
            disabled={busy}
            onPress={onCancel}
        />
    )

    if (recap != null) {
        return (
            <Overlay visible={visible} onRequestClose={onDismiss ?? onCancel} placement="center" testID={testID}>
                <View style={styles.card}>
                    <Stack gap="4">
                        <Title size="text-lg" center>
                            {title}
                        </Title>
                        {message != null && (
                            <Text color="text-muted" center>
                                {message}
                            </Text>
                        )}
                        <RecapList lines={recap} />
                        {children}
                        <Stack gap="3">
                            <Button
                                label={confirmLabel}
                                color={danger ? "danger" : undefined}
                                onPress={onConfirm}
                                loading={confirmLoading}
                                fullWidth
                            />
                            {!hideCancel && (
                                <Button
                                    label={cancelLabel}
                                    variant="outline"
                                    onPress={onCancel}
                                    disabled={confirmLoading}
                                    fullWidth
                                />
                            )}
                        </Stack>
                    </Stack>
                </View>
            </Overlay>
        )
    }

    return (
        <Overlay visible={visible} onRequestClose={onDismiss ?? onCancel} placement="center" testID={testID}>
            <View style={[styles.card, secondary != null && styles.stackedCard]}>
                <Stack gap="4">
                    <Stack gap="2">
                        <Title size="text-lg">{title}</Title>
                        {message != null && <Text color="text-muted">{message}</Text>}
                    </Stack>
                    {children}
                    {/* À côté d'une confirmation rouge, l'annulation reste au marine du socle : le bouton
                        de l'élève est orange, et deux aplats chauds côte à côte feraient de la
                        sortie une seconde action destructrice. */}
                    {secondary == null ? (
                        <Row gap="2" justify="end">
                            {cancel}
                            {confirm}
                        </Row>
                    ) : (
                        <Stack gap="2">
                            {confirm}
                            <Button
                                label={secondary.label}
                                variant="outline"
                                fullWidth
                                loading={secondaryLoading}
                                disabled={busy && !secondaryLoading}
                                onPress={secondary.onPress}
                            />
                            {cancel}
                        </Stack>
                    )}
                </Stack>
            </View>
        </Overlay>
    )
}

const styles = StyleSheet.create((theme) => ({
    card: {
        width: 320,
        maxWidth: "90%" as const,
        backgroundColor: theme.colors["surface-raised"],
        borderRadius: theme.radius.lg,
        padding: theme.spacing["5"],
    },
    stackedCard: { width: 360 },
}))
